import Hyperbeam from '@hyperbeam/web';

import { HyperbeamSession } from '@/survey-graphql';

const IFRAME_CHECK_API_PATH = '/survey/api/check-iframe-embeddable';

type HyperbeamInstance = Awaited<ReturnType<typeof Hyperbeam>>;

interface BrowserSessionValue {
  instance: HyperbeamInstance | null;
  sessionId: string | null;
  containerElement: HTMLDivElement | null;
}

/**
 * Check if URL can be embedded in iframe using server-side API.
 * Uses /survey/api path to work with manager app proxy configuration.
 */
async function checkIframeEmbeddable(url: string): Promise<boolean> {
  try {
    const response = await fetch(
      `${IFRAME_CHECK_API_PATH}?url=${encodeURIComponent(url)}`
    );

    let data;
    try {
      data = await response.json();
    } catch {
      return true;
    }

    if (data.canEmbed === false) {
      return false;
    }

    if (response.ok) {
      return data.canEmbed ?? true;
    }

    return true;
  } catch {
    return true;
  }
}

export function checkIframeEmbeddabilityOnce({
  url,
  setCanEmbed,
  setIsChecking,
}: {
  url: string | null;
  setCanEmbed: (value: boolean | null) => void;
  setIsChecking: (value: boolean) => void;
}): (() => void) | undefined {
  if (!url) return undefined;

  let isMounted = true;

  const check = async () => {
    const result = await checkIframeEmbeddable(url);

    if (isMounted) {
      setCanEmbed(result);
      setIsChecking(false);
    }
  };

  void check();

  return () => {
    isMounted = false;
  };
}

export function createHyperbeamSession({
  browserSession,
  containerElement,
  contentUrl,
  isInitializingRef,
  currentUrlRef,
  setIsLoading,
  setError,
  setBrowserSession,
  onCreateSession,
  onRemoveSession,
  errorMessage,
}: {
  browserSession: BrowserSessionValue;
  containerElement: HTMLDivElement | null;
  contentUrl: string | null;
  isInitializingRef: React.RefObject<boolean>;
  currentUrlRef: React.RefObject<string | null>;
  setIsLoading: (value: boolean) => void;
  setError: (value: string | null) => void;
  setBrowserSession: (value: BrowserSessionValue) => void;
  onCreateSession: (startUrl: string) => Promise<HyperbeamSession>;
  onRemoveSession: (sessionId: string) => Promise<void>;
  errorMessage: string;
}): (() => void) | undefined {
  if (
    browserSession.instance ||
    !containerElement ||
    isInitializingRef.current
  ) {
    return undefined;
  }

  let isMounted = true;
  isInitializingRef.current = true;

  const init = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const startUrl = contentUrl || 'about:blank';
      const session = await onCreateSession(startUrl);

      if (!isMounted) {
        void onRemoveSession(session.sessionId);
        return;
      }

      const hb = await Hyperbeam(containerElement, session.embedUrl, {
        adminToken: session.adminToken || undefined,
      });

      if (!isMounted) {
        hb.destroy();
        void onRemoveSession(session.sessionId);
        return;
      }

      setBrowserSession({
        instance: hb,
        sessionId: session.sessionId,
        containerElement,
      });

      currentUrlRef.current = startUrl;
    } catch {
      if (isMounted) {
        setError(errorMessage);
      }
    } finally {
      isInitializingRef.current = false;
      if (isMounted) {
        setIsLoading(false);
      }
    }
  };

  void init();

  return () => {
    isMounted = false;
  };
}

export function navigateHyperbeamTabs({
  contentUrl,
  instance,
  currentUrlRef,
}: {
  contentUrl: string | null;
  instance: HyperbeamInstance | null;
  currentUrlRef: React.RefObject<string | null>;
}): void {
  if (!contentUrl || !instance) {
    return;
  }

  try {
    void instance.tabs.update({ url: contentUrl });
    currentUrlRef.current = contentUrl;
  } catch {
    // Ignore navigation errors - don't show error to user
  }
}
